import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight, Checkmark } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { useEffect, type ReactNode } from 'react';

import { PaymentSucceeded } from 'src/ampli';
import { AnalyticsCoupons } from 'src/analytics';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { MONTHLY_CONNECT_PRICE_IN_USD_FOR_TRACKING } from 'src/analytics/utils';
import type {
  CloudConnectSubscription,
  CheckoutPrice,
  ConnectInterval,
  Price,
} from 'src/api/endpoints/cloudConnect.types';
import { useCheckoutConfirmationV2 } from 'src/api/queries';
import guideImageDesktop from 'src/assets/img/learning/guide-image-desktop.png';
import guideImageMobile from 'src/assets/img/learning/guide-image-mobile.png';
import methodsImageDesktop from 'src/assets/img/learning/methods-image-desktop.png';
import methodsImageMobile from 'src/assets/img/learning/methods-image-mobile.png';
import {
  CardContent,
  Feedback,
  Sheet,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { Link2 } from 'src/components/Link2';
import { LinkExternal } from 'src/components/LinkExternal';
import { PriceConnect } from 'src/components/PriceConnect';
import { StatusTag } from 'src/components/StatusTag';
import { COUNTRIES, useCountryLabel } from 'src/utils/countryList';
import { formatDate } from 'src/utils/formatDate';
import { useFreeTrialLengthText } from 'src/utils/freeTrialLength';
import { URLS } from 'src/utils/urls/urls';

import { FreeConnectTitle, freePromotionMonths } from './FreeConnectTitle';

function PaymentInformation({
  subscription,
}: Readonly<{
  subscription: CloudConnectSubscription;
}>) {
  const { t } = useLingui();
  const paymentMethod = subscription.paymentMethod;

  if (!paymentMethod) return null;

  switch (paymentMethod.type) {
    case 'card': {
      const cardBrand = paymentMethod.card?.brand ?? t`N/A`;
      const cardLast4 = paymentMethod.card?.last4 ?? t`(Not available)`;
      const cardExpirationMonth = String(
        paymentMethod.card?.expireMonth
      ).padStart(2, '0');
      const cardExpirationYear = paymentMethod.card?.expireYear ?? '';

      return (
        <>
          <div className="flex flex-row items-center justify-between gap-16">
            <p className="m-0 text-14">
              <Trans>Payment card</Trans>
            </p>
            <p
              className="m-0 text-right text-14"
              data-cy="checkout-payment-method"
            >
              {cardBrand} *{cardLast4}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between gap-16">
            <p className="m-0 text-14">
              <Trans>Valid to</Trans>
            </p>
            <p className="m-0 text-right text-14">
              {cardExpirationMonth} / {cardExpirationYear}
            </p>
          </div>
        </>
      );
    }
    case 'paypal':
      return (
        <div className="flex flex-row items-center justify-between gap-16">
          <p className="m-0 text-14">
            <Trans>Payment method</Trans>
          </p>
          <p
            className="m-0 text-right text-14"
            data-cy="checkout-payment-method"
          >
            {paymentMethod.paypal?.email ?? t`PayPal`}
          </p>
        </div>
      );
    case 'invoice':
      return (
        <p className="m-0 text-14">
          <Trans>Paying with invoice</Trans>
        </p>
      );
    default:
      return null;
  }
}

const getCurrentPrice = ({
  price,
  interval,
  isFree,
}: {
  price: CheckoutPrice;
  interval: ConnectInterval;
  isFree: boolean;
}): Price => ({
  currency: price.currency,
  precision: price.precision,
  quantity: 1,
  unitAmount: price.taxIncluded
    ? price.totalAmount
    : price.totalAmountExcludingTax,
  totalAmount: isFree ? 0 : price.totalAmount,
  period: interval,
});

const getTaxAmountPrice = (price: CheckoutPrice): Price => ({
  currency: price.currency,
  precision: price.precision,
  quantity: 1,
  unitAmount: price.taxAmount,
  totalAmount: price.taxAmount,
});

function PlanTitle({
  freeTrialDays,
  freeMonths,
}: Readonly<{ freeTrialDays: number; freeMonths?: number }>) {
  const { t } = useLingui();
  const freeTrialLengthText = useFreeTrialLengthText(freeTrialDays);

  if (freeMonths !== undefined) {
    return <FreeConnectTitle months={freeMonths} />;
  }

  if (freeTrialDays > 0) {
    return freeTrialLengthText;
  }

  return t`Connect subscription`;
}

function SubscriptionPageNote({
  freeTrialDays,
  hasFreePromotion,
}: Readonly<{ freeTrialDays: number; hasFreePromotion: boolean }>) {
  if (hasFreePromotion) {
    return (
      <Trans>
        We will send you a reminder before your first payment is due. View your
        plan or update your payment card details in the{' '}
        <Link2
          to={URLS.SUBSCRIPTION}
          inline
          componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
        >
          Subscription page
        </Link2>
        .
      </Trans>
    );
  }

  if (freeTrialDays > 0) {
    return (
      <Trans>
        We&apos;ll send you a reminder email before your free trial ends. View
        your plan or update your payment card details in the{' '}
        <Link2
          to={URLS.SUBSCRIPTION}
          inline
          componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
        >
          Subscription page
        </Link2>
        .
      </Trans>
    );
  }

  return (
    <Trans>
      View your plan or update your payment card details in the{' '}
      <Link2
        to={URLS.SUBSCRIPTION}
        inline
        componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
      >
        Subscription page
      </Link2>
      .
    </Trans>
  );
}

function WhatsNextCard({
  desktopImage,
  mobileImage,
  imageAlt,
  title,
  description,
  to,
}: Readonly<{
  desktopImage: string;
  mobileImage: string;
  imageAlt: string;
  title: string;
  description: ReactNode;
  to: string;
}>) {
  return (
    <LinkExternal
      to={to}
      componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
      className="rounded-2 group relative flex h-full min-h-[260px] w-full flex-col justify-end overflow-hidden no-underline hover:no-underline"
    >
      <picture>
        {/* Matches the `lm` Tailwind breakpoint where cards switch from a
            single, full-width column to a two-column grid. */}
        <source media="(min-width: 640px)" srcSet={desktopImage} />
        <img
          src={mobileImage}
          alt={imageAlt}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="relative flex w-full items-end justify-between gap-16 p-24">
        <div className="flex flex-col gap-8 text-left text-white">
          <Typography
            as="span"
            variant="responsive-heading-subtle-md"
            className="m-0 text-white"
          >
            {title}
          </Typography>
          <Typography as="span" variant="body-sm" className="m-0 text-white">
            {description}
          </Typography>
        </div>
        <span className="flex h-48 w-48 shrink-0 items-center justify-center rounded-full bg-white text-black transition-transform group-hover:translate-x-2">
          <ArrowRight />
        </span>
      </div>
    </LinkExternal>
  );
}

function CheckoutConfirmationDetails({
  data,
}: Readonly<{
  data: NonNullable<ReturnType<typeof useCheckoutConfirmationV2>['data']>;
}>) {
  const { t, i18n } = useLingui();
  const getCountryLabel = useCountryLabel();

  const subscription = data.subscription;
  const freeTrialDays = data.freeTrialDays;
  const price = data.price;
  const promotion = data.promotion;

  useEffect(() => {
    // The confirmation page can be reloaded; only report each order once.
    const storageKey = `payment-succeeded-${data.orderId}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, 'true');

    tracker.trackEvent(
      new PaymentSucceeded({
        order_id: data.orderId,
        currency: price.currency,
        products: [
          {
            sku: { value: 'connect' },
            name: { value: 'Connect' },
            quantity: { value: 1 },
          },
        ],
        // Same constant USD revenue as the legacy checkout, for comparability.
        revenue: MONTHLY_CONNECT_PRICE_IN_USD_FOR_TRACKING.toString(),
        // The coupon name is tracked rather than the customer-facing code.
        coupon: promotion
          ? promotion.appliedPromotion.name
          : freeTrialDays > 0
            ? AnalyticsCoupons.CONNECT_OFFER
            : AnalyticsCoupons.NONE,
        payment_method: subscription.paymentMethod?.type,
      })
    );
  }, [data.orderId]);

  const freeMonths = promotion
    ? freePromotionMonths(promotion.appliedPromotion)
    : undefined;
  const hasFreePromotion = freeMonths !== undefined;
  // With a free promotion the first invoice that is not $0 is when the discount ends.
  const nextBillingDate =
    hasFreePromotion && promotion?.endsAt
      ? promotion.endsAt
      : subscription.periodEnd;
  const currentPrice = getCurrentPrice({
    price,
    interval: subscription.planCurrent.interval,
    isFree: freeTrialDays > 0 || hasFreePromotion,
  });

  const country = COUNTRIES.find(
    (country) => country.value === subscription.address.country
  );
  const countryName = country
    ? getCountryLabel(country.value)
    : subscription.address.country;
  const intervalLabels: Record<ConnectInterval, string> = {
    annual: t`annually`,
    biennial: t`biennially`,
    monthly: t`monthly`,
  };
  const intervalLabel = intervalLabels[subscription.planCurrent.interval];

  const taxLabel =
    price.taxType === 'VAT'
      ? t`VAT to ${countryName}`
      : price.taxType === 'GST'
        ? t`GST to ${countryName}`
        : t`Sales tax to ${countryName}`;
  const taxAmountPrice = getTaxAmountPrice(price);

  return (
    <AppPage.Container>
      <AppPage.Content className="max-w-l2xl gap-48 pt-24 lm:gap-64 lm:pt-64">
        <div className="flex flex-col items-center gap-12 text-center">
          <div className="flex flex-col items-center gap-16 lm:flex-row">
            <div className="flex h-40 w-40 items-center justify-center rounded-full bg-[#CEF0CD]">
              <Checkmark size={20} className="text-neutral-dark-8" />
            </div>
            <Typography
              as="h1"
              variant="responsive-heading-lg"
              fontWeight="regular"
              className="m-0"
            >
              <Trans>Order confirmed</Trans>
            </Typography>
          </div>
          <Typography variant="body-md" className="m-0">
            <Trans>
              Thanks for your order, and congratulations on getting Connect!
            </Trans>
          </Typography>
        </div>

        <div className="flex flex-col gap-32 lm:flex-row lm:gap-64">
          <div className="flex flex-1 flex-col gap-24 border-b border-neutral-light-4 pb-32 lm:pb-0">
            <Typography
              as="h2"
              variant="heading-sm"
              fontWeight="regular"
              className="m-0"
            >
              <Trans>Your details</Trans>
            </Typography>
            <div className="grid grid-cols-[minmax(80px,auto)_1fr] gap-x-24 gap-y-12">
              <p className="m-0 text-16 font-medium">
                <Trans>Name</Trans>
              </p>
              <p className="m-0 text-16" data-cy="confirmation-details-name">
                {subscription.name}
              </p>
              <p className="m-0 text-16 font-medium">
                <Trans>Country</Trans>
              </p>
              <p className="m-0 text-16" data-cy="confirmation-details-country">
                {countryName}
              </p>
              <p className="m-0 text-16 font-medium">
                <Trans>Email</Trans>
              </p>
              <p
                className="m-0 text-16 break-all"
                data-cy="confirmation-details-email"
              >
                {subscription.email}
              </p>
            </div>
            <Typography variant="body-sm" className="m-0 text-text-muted">
              <Trans>
                If you need to add or edit billing information you can do so in
                the{' '}
                <Link2
                  to={URLS.SUBSCRIPTION_PAYMENT}
                  inline
                  componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
                >
                  Payment page
                </Link2>
                .
              </Trans>
            </Typography>
          </div>

          <div className="flex flex-1 flex-col gap-16">
            <Typography
              as="h2"
              variant="heading-sm"
              fontWeight="regular"
              className="m-0"
            >
              <Trans>Your plan</Trans>
            </Typography>
            <Sheet data-cy="confirmation-card" className="w-full bg-paper-3">
              <CardContent>
                <div className="flex flex-row items-start justify-between gap-12 lm:items-center">
                  <div className="flex flex-col items-start gap-8 lm:flex-row lm:items-center lm:gap-12">
                    <Typography
                      variant="body-lg"
                      fontWeight="medium"
                      leadingNone
                      data-cy="confirmation-card-heading-title"
                    >
                      <PlanTitle
                        freeTrialDays={freeTrialDays}
                        freeMonths={freeMonths}
                      />
                    </Typography>
                    <StatusTag
                      status={freeTrialDays > 0 ? 'Trialing' : 'Active'}
                    />
                  </div>
                  <Typography
                    variant="body-lg"
                    fontWeight="medium"
                    leadingNone
                    data-cy="connect-plan-row-price-amount"
                  >
                    <PriceConnect
                      price={currentPrice}
                      breakBeforePeriodOnMobile
                    />
                  </Typography>
                </div>
              </CardContent>
              <CardContent borderTop>
                <div className="flex flex-col gap-8">
                  {price.taxType && (
                    <div className="flex flex-row items-center justify-between gap-16">
                      <p className="m-0 text-14">{taxLabel}</p>
                      <p className="m-0 text-right text-14">
                        {price.taxIncluded ? (
                          <Trans
                            context="Tax"
                            comment="Whether tax is included in the price and not listed separately."
                          >
                            Included
                          </Trans>
                        ) : (
                          <PriceConnect price={taxAmountPrice} />
                        )}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-row items-center justify-between gap-16">
                    <p className="m-0 text-14">
                      {t`Next billing (${intervalLabel})`}
                    </p>
                    <p
                      data-cy="checkout-summary-billing-start-date"
                      className="m-0 text-right text-14"
                    >
                      {formatDate(nextBillingDate, {
                        locale: i18n.locale,
                      })}
                    </p>
                  </div>
                  <div className="flex flex-row justify-between gap-16">
                    <p className="m-0 text-14 whitespace-nowrap">
                      <Trans>Order ID</Trans>
                    </p>
                    <p
                      className="m-0 text-right text-14"
                      data-cy="checkout-order-id"
                    >
                      {data.orderId}
                    </p>
                  </div>
                  {promotion && (
                    <div className="flex flex-row justify-between gap-16">
                      <p className="m-0 text-14 whitespace-nowrap">
                        <Trans>Promo code</Trans>
                      </p>
                      <p
                        className="m-0 text-right text-14"
                        data-cy="checkout-promo-code"
                      >
                        {promotion.code}
                      </p>
                    </div>
                  )}

                  <PaymentInformation subscription={subscription} />
                </div>
              </CardContent>
              <CardContent borderTop>
                <p className="m-0 text-14">
                  <SubscriptionPageNote
                    freeTrialDays={freeTrialDays}
                    hasFreePromotion={hasFreePromotion}
                  />
                </p>
              </CardContent>
            </Sheet>
          </div>
        </div>

        <Sheet className="gap-24 rounded-[4px]">
          <div className="flex flex-col gap-8">
            <Typography
              as="h2"
              variant="heading-sm"
              fontWeight="regular"
              className="m-0"
            >
              <Trans>What&apos;s next?</Trans>
            </Typography>
            <Typography variant="body-md" className="m-0">
              <Trans>
                Kick start your paper tablet experience with these tips.
              </Trans>
            </Typography>
          </div>
          <div className="grid grid-cols-1 gap-32 lm:grid-cols-2">
            <WhatsNextCard
              desktopImage={guideImageDesktop}
              mobileImage={guideImageMobile}
              imageAlt={t`A reMarkable paper tablet on a table`}
              title={t`Getting started`}
              description={t`Try our easy 6-step guide.`}
              to={URLS.RM_COM_GETTING_STARTED_STEP_1}
            />
            <WhatsNextCard
              desktopImage={methodsImageDesktop}
              mobileImage={methodsImageMobile}
              imageAlt={t`Hands using a reMarkable paper tablet with templates`}
              title={t`Download templates`}
              description={
                <Trans>
                  Visit Methods, our go-to
                  <br className="lm:hidden" /> resource for
                  <br className="hidden lm:inline" /> premium
                  <br className="lm:hidden" /> templates.
                </Trans>
              }
              to={URLS.RM_COM_ABOUT_METHODS}
            />
          </div>
        </Sheet>

        <Feedback
          title={t`Did you find what you were looking for?`}
          feedbackTargetId="store-connect-confirmation"
        />
      </AppPage.Content>
    </AppPage.Container>
  );
}

export const CheckoutConfirmationV2 = () => {
  const confirmation = useCheckoutConfirmationV2();

  if (confirmation.isPending) {
    return <Spinner />;
  }

  if (confirmation.isError || !confirmation.data) {
    return <SomethingWentWrong error={confirmation.error} />;
  }

  return <CheckoutConfirmationDetails data={confirmation.data} />;
};
